const FOLDER_ID = import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID

export interface DrivePOAFile {
  fileId: string
  fileName: string
  agentName: string
  webViewLink: string
  createdTime: string
  mimeType: string
}

type DriveFile = { id: string; name: string; webViewLink: string; createdTime: string; mimeType: string }

async function fetchFiles(folderId: string, driveId: string | undefined, accessToken: string, mimeTypes: string[]): Promise<DriveFile[]> {
  const mimeQuery = mimeTypes.map(mt => `mimeType='${mt}'`).join(' or ')
  const url = driveId
    ? `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+(${mimeQuery})+and+trashed=false&fields=files(id,name,webViewLink,createdTime,mimeType)&pageSize=100&corpora=drive&driveId=${driveId}&supportsAllDrives=true&includeItemsFromAllDrives=true`
    : `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+(${mimeQuery})+and+trashed=false&fields=files(id,name,webViewLink,createdTime,mimeType)&pageSize=100&supportsAllDrives=true&includeItemsFromAllDrives=true`
  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
  if (!res.ok) return []
  const data = await res.json()
  return data.files || []
}

async function fetchSubfolders(folderId: string, driveId: string | undefined, accessToken: string): Promise<{ id: string; name: string }[]> {
  const url = driveId
    ? `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+mimeType='application/vnd.google-apps.folder'+and+trashed=false&fields=files(id,name)&pageSize=100&corpora=drive&driveId=${driveId}&supportsAllDrives=true&includeItemsFromAllDrives=true`
    : `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+mimeType='application/vnd.google-apps.folder'+and+trashed=false&fields=files(id,name)&pageSize=100&supportsAllDrives=true&includeItemsFromAllDrives=true`
  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
  if (!res.ok) return []
  const data = await res.json()
  return data.files || []
}

export async function scanPOAFolder(accessToken: string): Promise<DrivePOAFile[]> {
  if (!FOLDER_ID) throw new Error('VITE_GOOGLE_DRIVE_FOLDER_ID is not configured — check your environment variables')

  const results: DrivePOAFile[] = []
  const SUPPORTED_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ]

  // Layer 1: list agent folders under root FOLDER_ID
  const foldersRes = await fetch(
    `https://www.googleapis.com/drive/v3/files?q='${FOLDER_ID}'+in+parents+and+mimeType='application/vnd.google-apps.folder'+and+trashed=false&fields=files(id,name)&pageSize=100&supportsAllDrives=true&includeItemsFromAllDrives=true`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
  if (!foldersRes.ok) {
    const errBody = await foldersRes.text()
    throw new Error(`Drive API error: ${foldersRes.status} - ${errBody}`)
  }
  const foldersData = await foldersRes.json()
  const agentFolders: { id: string; name: string }[] = foldersData.files || []

  for (const agentFolder of agentFolders) {
    try {
      // Get driveId from folder metadata (needed for Shared Drive queries)
      const metaRes = await fetch(
        `https://www.googleapis.com/drive/v3/files/${agentFolder.id}?fields=id,driveId&supportsAllDrives=true`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      const meta = await metaRes.json()
      const driveId: string | undefined = meta.driveId

      // Layer 2a: Supported files directly inside the agent folder
      const directFiles = await fetchFiles(agentFolder.id, driveId, accessToken, SUPPORTED_MIME_TYPES)

      // Layer 2b: subfolders inside the agent folder
      const subfolders = await fetchSubfolders(agentFolder.id, driveId, accessToken)

      // Layer 3: Supported files inside each subfolder
      const subFiles: DriveFile[] = []
      for (const sub of subfolders) {
        const files = await fetchFiles(sub.id, driveId, accessToken, SUPPORTED_MIME_TYPES)
        subFiles.push(...files)
      }

      // Combine all files under this agent name
      const allFiles = [...directFiles, ...subFiles]
      console.log(`[Drive Scan] Agent "${agentFolder.name}": found ${allFiles.length} files (${directFiles.length} direct, ${subFiles.length} in subfolders)`)
      for (const file of allFiles) {
        results.push({
          fileId: file.id,
          fileName: file.name,
          agentName: agentFolder.name,
          webViewLink: file.webViewLink,
          createdTime: file.createdTime,
          mimeType: file.mimeType,
        })
        console.log(`[Drive Scan]   - ${file.name} (ID: ${file.id}, type: ${file.mimeType})`)
      }
    } catch (e) {
      console.error(`[Drive Scan] Error scanning agent folder "${agentFolder.name}":`, e)
    }
  }
  console.log(`[Drive Scan] Total files found: ${results.length}`)

  return results
}

export async function getDriveFileBase64(fileId: string, accessToken: string): Promise<string> {
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
  if (!res.ok) throw new Error(`Failed to download file: ${res.statusText}`)
  const blob = await res.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export interface DriveFileContent {
  base64?: string
  text?: string
  fileType: 'pdf' | 'image' | 'docx' | 'xlsx' | 'unknown'
}

export async function getDriveFileContent(fileId: string, mimeType: string, accessToken: string): Promise<DriveFileContent> {
  if (mimeType === 'application/pdf') {
    const base64 = await getDriveFileBase64(fileId, accessToken)
    return { base64, fileType: 'pdf' }
  }

  if (mimeType.startsWith('image/')) {
    const base64 = await getDriveFileBase64(fileId, accessToken)
    return { base64, fileType: 'image' }
  }

  if (mimeType.includes('wordprocessingml')) {
    const text = await getDriveFileText(fileId, 'text/plain', accessToken)
    return { text, fileType: 'docx' }
  }

  if (mimeType.includes('spreadsheetml')) {
    const text = await getDriveFileText(fileId, 'text/csv', accessToken)
    return { text, fileType: 'xlsx' }
  }

  return { fileType: 'unknown' }
}

async function getDriveFileText(fileId: string, exportMimeType: string, accessToken: string): Promise<string> {
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${encodeURIComponent(exportMimeType)}&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
  if (!res.ok) throw new Error(`Failed to export file: ${res.statusText}`)
  return await res.text()
}
